import { Injectable, signal } from '@angular/core';
import type { DeliveryTrackerDocument } from '@features/organization/components/delivery-tracker-detail/types/delivery-tracker-document.types';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';

@Injectable()
export class DeliveryTrackerDocumentStore {
  private readonly deliveringTrackerIdState = signal<UUID | undefined>(undefined);

  public readonly deliveringTrackerId = this.deliveringTrackerIdState.asReadonly();

  private readonly deletingDocumentIdState = signal<UUID | undefined>(undefined);

  public readonly deletingDocumentId = this.deletingDocumentIdState.asReadonly();

  private readonly documentsByTrackerId = signal<Record<UUID, DeliveryTrackerDocument[]>>({});

  private readonly loadingByTrackerId = signal<Record<UUID, boolean>>({});

  /**
   * Returns the cached documents of one tracker.
   *
   * @param {UUID} trackerId - The tracker to read.
   * @returns {DeliveryTrackerDocument[]} The cached documents, empty when nothing is cached yet.
   */
  public getDocumentsForTracker(trackerId: UUID): DeliveryTrackerDocument[] {
    return this.documentsByTrackerId()[trackerId] ?? [];
  }

  /**
   * Whether documents were already fetched for one tracker, including a fetch that returned none.
   *
   * @param {UUID} trackerId - The tracker to check.
   * @returns {boolean} True once a fetch for that tracker has completed.
   */
  public hasDocumentsForTracker(trackerId: UUID): boolean {
    return Object.hasOwn(this.documentsByTrackerId(), trackerId);
  }

  /**
   * Replaces the cached documents of one tracker.
   *
   * @param {UUID} trackerId - The tracker the documents belong to.
   * @param {DeliveryTrackerDocument[]} documents - The documents to cache.
   */
  public setDocumentsForTracker(trackerId: UUID, documents: DeliveryTrackerDocument[]): void {
    this.documentsByTrackerId.update((map) => ({ ...map, [trackerId]: documents }));
  }

  /**
   * Whether one tracker's documents are currently being fetched.
   *
   * @param {UUID} trackerId - The tracker to check.
   * @returns {boolean} True while a fetch is in flight.
   */
  public isLoadingForTracker(trackerId: UUID): boolean {
    return this.loadingByTrackerId()[trackerId] ?? false;
  }

  /**
   * Flags one tracker's documents as loading.
   *
   * @param {UUID} trackerId - The tracker being fetched.
   * @param {boolean} isLoading - The new loading state.
   */
  public setLoadingForTracker(trackerId: UUID, isLoading: boolean): void {
    this.loadingByTrackerId.update((map) => ({ ...map, [trackerId]: isLoading }));
  }

  /**
   * Marks the tracker whose result delivery is currently in flight, or clears it.
   *
   * @param {UUID | undefined} trackerId - The delivering tracker, or undefined when none is.
   */
  public setDeliveringTrackerId(trackerId: UUID | undefined): void {
    this.deliveringTrackerIdState.set(trackerId);
  }

  /**
   * Marks the document whose deletion is currently in flight, or clears it.
   *
   * @param {UUID | undefined} documentId - The document being deleted, or undefined when none is.
   */
  public setDeletingDocumentId(documentId: UUID | undefined): void {
    this.deletingDocumentIdState.set(documentId);
  }
}
